import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import Icon from '../../../components/AppIcon';

import Input from '../../../components/ui/Input';

const CustomersTab = () => {
  const { user } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerQuotes, setCustomerQuotes] = useState([]);
  const [customerActivities, setCustomerActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (user?.id) {
      loadCustomers();
    }
  }, [user]);

  useEffect(() => {
    filterCustomers();
  }, [customers, searchTerm]);

  useEffect(() => {
    if (selectedCustomer?.id) {
      loadCustomerDetails(selectedCustomer?.id);
    }
  }, [selectedCustomer]);

  const loadCustomers = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase?.from('crm_customers')?.select('*')?.eq('provider_id', user?.id)?.order('created_at', { ascending: false });

      if (error) throw error;
      setCustomers(data || []);
    } catch (error) {
      console.error('Error loading customers:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCustomerDetails = async (customerId) => {
    try {
      // Load customer quotes
      const { data: quotes, error: quotesError } = await supabase?.from('provider_quotes')?.select('*')?.eq('customer_id', customerId)?.order('quote_date', { ascending: false });

      if (quotesError) throw quotesError;
      setCustomerQuotes(quotes || []);

      // Load customer activities
      const { data: activities, error: activitiesError } = await supabase?.from('crm_activities')?.select('*')?.eq('customer_id', customerId)?.order('activity_date', { ascending: false })?.limit(10);

      if (activitiesError) throw activitiesError;
      setCustomerActivities(activities || []);
    } catch (error) {
      console.error('Error loading customer details:', error);
    }
  };

  const filterCustomers = () => {
    if (!searchTerm) {
      setFilteredCustomers(customers);
      return;
    }

    const filtered = customers?.filter(
      (c) =>
        c?.customer_name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
        c?.customer_email?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
        c?.customer_phone?.includes(searchTerm)
    );

    setFilteredCustomers(filtered);
  };

  const getEngagementBadge = (level) => {
    const styles = {
      low: 'bg-muted text-muted-foreground border-border',
      medium: 'bg-warning/10 text-warning border-warning/20',
      high: 'bg-success/10 text-success border-success/20',
    };
    const labels = {
      low: 'Laag',
      medium: 'Gemiddeld',
      high: 'Hoog',
    };
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${styles?.[level]}`}>
        {labels?.[level]}
      </span>
    );
  };

  const getActivityIcon = (type) => {
    const icons = {
      email: 'Mail',
      call: 'Phone',
      meeting: 'Calendar',
      note: 'FileText',
      quote_sent: 'Send',
      quote_accepted: 'CheckCircle',
      quote_rejected: 'XCircle',
      installation_scheduled: 'Clock',
      installation_completed: 'Check',
    };
    return icons?.[type] || 'FileText';
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse space-y-4">
          {[1, 2, 3]?.map((i) => (
            <div key={i} className="h-16 bg-muted rounded"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-heading font-bold text-foreground">Klantenbeheer</h2>
          <p className="text-muted-foreground mt-1">Beheer klantprofielen en interactiegeschiedenis</p>
        </div>
      </div>

      {/* Search */}
      <div className="bg-card border border-border rounded-lg p-4">
        <Input
          placeholder="Zoek op naam, email of telefoon..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e?.target?.value)}
        />
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Customer List */}
        <div className="lg:col-span-1 bg-card border border-border rounded-lg overflow-hidden">
          <div className="p-4 border-b border-border">
            <h3 className="font-heading font-semibold text-foreground">Klanten ({filteredCustomers?.length})</h3>
          </div>
          <div className="divide-y divide-border max-h-[600px] overflow-y-auto">
            {filteredCustomers?.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">
                <p>Geen klanten gevonden</p>
              </div>
            ) : (
              filteredCustomers?.map((customer) => (
                <div
                  key={customer?.id}
                  onClick={() => setSelectedCustomer(customer)}
                  className={`p-4 cursor-pointer hover:bg-muted/30 transition-colors ${
                    selectedCustomer?.id === customer?.id ? 'bg-primary/5 border-l-4 border-l-primary' : ''
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="font-medium text-foreground">{customer?.customer_name}</p>
                      <p className="text-xs text-muted-foreground mt-1">{customer?.customer_email}</p>
                      <p className="text-xs text-muted-foreground">{customer?.customer_phone}</p>
                    </div>
                    <div className="ml-2">{getEngagementBadge(customer?.engagement_level)}</div>
                  </div>
                  <div className="mt-2 flex items-center space-x-4 text-xs text-muted-foreground">
                    <span>LTV: €{customer?.lifetime_value?.toLocaleString('nl-NL')}</span>
                    <span>Score: {customer?.lead_score}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Customer Details */}
        <div className="lg:col-span-2 space-y-6">
          {!selectedCustomer ? (
            <div className="bg-card border border-border rounded-lg p-12 text-center">
              <Icon name="Users" size={48} className="text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">Selecteer een klant om details te bekijken</p>
            </div>
          ) : (
            <>
              {/* Customer Info Card */}
              <div className="bg-card border border-border rounded-lg p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-xl font-heading font-bold text-foreground">{selectedCustomer?.customer_name}</h3>
                    <p className="text-muted-foreground mt-1">{selectedCustomer?.customer_email}</p>
                  </div>
                  {getEngagementBadge(selectedCustomer?.engagement_level)}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Telefoon</p>
                    <p className="text-sm text-foreground">{selectedCustomer?.customer_phone || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Adres</p>
                    <p className="text-sm text-foreground">{selectedCustomer?.customer_address || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Postcode / Stad</p>
                    <p className="text-sm text-foreground">
                      {selectedCustomer?.postal_code} {selectedCustomer?.city}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Woningtype</p>
                    <p className="text-sm text-foreground">{selectedCustomer?.property_type || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Lifetime Value</p>
                    <p className="text-sm font-medium text-success">
                      €{selectedCustomer?.lifetime_value?.toLocaleString('nl-NL')}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Lead Score</p>
                    <p className="text-sm font-medium text-primary">{selectedCustomer?.lead_score}/100</p>
                  </div>
                </div>

                {selectedCustomer?.notes && (
                  <div className="mt-4 pt-4 border-t border-border">
                    <p className="text-xs text-muted-foreground mb-1">Notities</p>
                    <p className="text-sm text-foreground">{selectedCustomer?.notes}</p>
                  </div>
                )}
              </div>

              {/* Customer Quotes */}
              <div className="bg-card border border-border rounded-lg p-6">
                <h4 className="font-heading font-semibold text-foreground mb-4 flex items-center space-x-2">
                  <Icon name="FileText" size={20} className="text-primary" />
                  <span>Offertes ({customerQuotes?.length})</span>
                </h4>
                {customerQuotes?.length === 0 ? (
                  <p className="text-muted-foreground text-sm">Geen offertes beschikbaar</p>
                ) : (
                  <div className="space-y-3">
                    {customerQuotes?.map((quote) => (
                      <div key={quote?.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                        <div>
                          <p className="text-sm font-medium text-foreground">{quote?.quote_number}</p>
                          <p className="text-xs text-muted-foreground capitalize">{quote?.project_type}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-medium text-foreground">
                            €{parseFloat(quote?.quote_amount)?.toLocaleString('nl-NL')}
                          </p>
                          <p className="text-xs text-muted-foreground capitalize">{quote?.status}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Customer Activities */}
              <div className="bg-card border border-border rounded-lg p-6">
                <h4 className="font-heading font-semibold text-foreground mb-4 flex items-center space-x-2">
                  <Icon name="Activity" size={20} className="text-primary" />
                  <span>Recente Activiteiten</span>
                </h4>
                {customerActivities?.length === 0 ? (
                  <p className="text-muted-foreground text-sm">Geen activiteiten beschikbaar</p>
                ) : (
                  <div className="space-y-3">
                    {customerActivities?.map((activity) => (
                      <div key={activity?.id} className="flex items-start space-x-3">
                        <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                          <Icon name={getActivityIcon(activity?.activity_type)} size={16} className="text-primary" />
                        </div>
                        <div className="flex-1">
                          <p className="text-sm text-foreground">{activity?.activity_description}</p>
                          <p className="text-xs text-muted-foreground mt-1">
                            {new Date(activity?.activity_date)?.toLocaleString('nl-NL')}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomersTab;